import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes';
import { hydratePermissionsIfMissing } from '@/utils/routeGuards';
import { useAuth } from '@/composables/useAuth';
import { isAuthenticated } from '@/stores/authStore';
import { updateLDContext, checkLDFlags } from '@/plugins/launchdarkly';
import { pageCustomerIo } from '@/plugins/customerio';
import { useUserStore } from '@/stores/userStore';

declare module 'vue-router' {
  interface RouteMeta {
    layout: 'LayoutAuthenticated' | 'LayoutDefault';
    layoutOptions?: Record<string, unknown>;
    title?: string;
    requiresAuth: boolean;
    requiredLDFlags?: string[];
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  // This means that we're using the back button to go back to the budget list, but due to the optionalTwrn, we're being directed to the individual budget page. However since there is no budget twrn, we're getting an error when trying to fetch a budget. However due to a Vue Router bug, we need to keep the param at optional level https://github.com/vuejs/router/issues/845
  if (to.path === '/budgets/roster-view' && !to.params.budgetTwrn) {
    return {
      name: 'TeamBudgets',
      query: { ...(to.query || {}) },
    };
  }
  // agreements too
  if (to.path === '/agreements/overview' && !to.params.agreementTwrn) {
    return {
      name: 'Agreements',
      query: { ...(to.query || {}) },
    };
  }

  if (import.meta.env.TEST) return;
  if (to.meta.requiresAuth) {
    if (!isAuthenticated()) {
      useAuth().logOut();
      return { name: 'UnauthenticatedView' };
    }

    await useUserStore().setAppSwitcher();
    await hydratePermissionsIfMissing();
    await updateLDContext();

    if (to.meta.requiredLDFlags?.length) {
      const hasAccess = await checkLDFlags(to.meta.requiredLDFlags);
      if (!hasAccess) {
        return { name: 'NoAccess' };
      }
    }
  }

  return true;
});

router.afterEach((to, from) => {
  if (from.name != null && to.path !== from.path) {
    document.getElementById('main-content')?.focus();
  }

  if (import.meta.env.TEST) return;
  if (!isAuthenticated()) return;
  if (to.path === from.path) return;
  pageCustomerIo(String(to.name ?? to.path), to.path);
});

router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Failed to load module script')) {
    (window as Window).location = to.fullPath;
  }
});

export default router;
